import { Dispatch, SetStateAction } from "react";
import { Chip, Grid2, useMediaQuery, useTheme } from "@mui/material";
import { CustomColoredTabs } from "../../../../../CustomColoredTabs/CustomColoredTabs.component";
import { BookingServiceCustomTab } from "./partial/BookingServiceCustomTab.partial";
import { TabPanel } from "../../../../../../features/TabPanel/TabPanel.component";
import BookingServiceCateringTable from "./Catering/BookingService-CateringTable.component";
import BookingServiceBookableEquipmentTable from "./BookableEquipment/BookingService-BookableEquipmentTable.component";
import BookingServiceRoomLayoutTable from "./RoomLayout/BookingService-RoomLayoutTable.component";
import { BookingServiceEditedData, ServiceOrderDto } from "./Domain/BookingServicePrompt.types";
import { BookingServiceCustomTapChipLabel } from "./partial/BookingServiceCustomTapChipLabel.partial";
import { hasMandatory } from "./functions/hasMandatory.function";
import { BookingServiceMapConfig } from "./BookingServicePromptDialog";

interface Props {
  mappedTabs: BookingServiceMapConfig[];
  editedService: BookingServiceEditedData;
  setEditedService: Dispatch<SetStateAction<BookingServiceEditedData>>;
  orderedService: ServiceOrderDto;
  setOrderedService: Dispatch<SetStateAction<ServiceOrderDto>>;
  selectTab: number;
  setSelectTab: (t: number) => void;
}

export function BookingServicePromptDialogContents({
  mappedTabs,
  editedService,
  setEditedService,
  orderedService,
  setOrderedService,
  selectTab,
  setSelectTab
}: Readonly<Props>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const tableSxStyle = {
    fontSize: isMobileTablet ? 12 : 17,
    fontWeight: theme.typography.fontWeightRegular,
    pl: 0.5,
    pr: 0.5,
    "&.Mui-selected": {
      color:
        theme.palette.mode === "dark"
          ? theme.palette.getContrastText(theme.palette.primary.main)
          : theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold
    }
  };

  const customTabChipColor = (index: number) => (selectTab === index ? "primary" : "secondary");
  const handleChange = (e: any, newValue: number) => setSelectTab(newValue);

  return (
    <>
      {/* tabs header */}
      <Grid2 container data-testid="booking-service-prompt-dialog-contents-tabs" mt={1.5}>
        <CustomColoredTabs value={selectTab} onChange={handleChange} autoScrollable hideIndicator>
          {mappedTabs.map(({ label, usedItems, mappedIndex = 0, testId }) => (
            <BookingServiceCustomTab
              key={testId}
              index={mappedIndex}
              value={mappedIndex}
              icon={
                <Chip
                  color={customTabChipColor(mappedIndex)}
                  label={<BookingServiceCustomTapChipLabel title={label} usedItems={usedItems} />}
                />
              }
              data-testid={testId}
              sx={tableSxStyle}
            />
          ))}
        </CustomColoredTabs>
      </Grid2>

      {/* tab contents of booking service prompt dialog */}
      {/* Catering list */}
      <TabPanel
        value={selectTab}
        index={mappedTabs.find(tab => tab.key === "catering")?.mappedIndex}
      >
        <BookingServiceCateringTable
          minHeight={isMobile ? "30vh" : "40vh"}
          editedService={editedService}
          setEditedService={setEditedService}
          orderedService={orderedService}
          setOrderedService={setOrderedService}
          isMobile={isMobile}
          isMandatory={hasMandatory(editedService.cateringData)}
          isTabSelected={selectTab === mappedTabs.find(tab => tab.key === "catering")?.mappedIndex}
        />
      </TabPanel>

      {/* Additional Equipment list */}
      <TabPanel
        value={selectTab}
        index={mappedTabs.find(tab => tab.key === "equipment")?.mappedIndex}
      >
        <BookingServiceBookableEquipmentTable
          minHeight={isMobile ? "30vh" : "40vh"}
          editedService={editedService}
          setEditedService={setEditedService}
          orderedService={orderedService}
          setOrderedService={setOrderedService}
          isMandatory={hasMandatory(editedService.bookableEquipmentData)}
          isTabSelected={selectTab === mappedTabs.find(tab => tab.key === "equipment")?.mappedIndex}
        />
      </TabPanel>

      {/* Flexible Room Layout list */}
      <TabPanel
        value={selectTab}
        index={mappedTabs.find(tab => tab.key === "roomLayout")?.mappedIndex}
      >
        <BookingServiceRoomLayoutTable
          minHeight={isMobile ? "30vh" : "40vh"}
          editedService={editedService}
          setEditedService={setEditedService}
          orderedService={orderedService}
          setOrderedService={setOrderedService}
          isMobile={isMobile}
          isMandatory={hasMandatory(editedService.roomLayoutData)}
          isTabSelected={
            selectTab === mappedTabs.find(tab => tab.key === "roomLayout")?.mappedIndex
          }
        />
      </TabPanel>
    </>
  );
}
